import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Save,
  ArrowLeft,
  Plus,
  X,
  Calendar,
  DollarSign,
  FileText,
  Heart,
  Thermometer,
  Weight,
  Eye,
  Stethoscope,
  Pill,
  ClipboardList,
  AlertCircle,
  CheckCircle,
  IndianRupee,
  ChevronLeft,
  ChevronRight,
  Edit,
} from "lucide-react";
import {
  createCheckUp,
  getCheckUpByAppointment,
  updateCheckUp,
} from "../../../services/checkUpApi";
import { getAppointmentById } from "../../../services/appointmentApi";

const DoctorCheckUpForm = () => {
  const { appointmentId } = useParams();
  const navigate = useNavigate();

  const [appointment, setAppointment] = useState(null);
  const [existingCheckUp, setExistingCheckUp] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState("examination");
  const [completedTabs, setCompletedTabs] = useState([]);
  const [isUpdateMode, setIsUpdateMode] = useState(false);
  const [weight, setWeight] = useState("");

  const [formData, setFormData] = useState({
    // Physical Examination
    physicalExamination: {
      weight: "",
      temperature: "",
      heartRate: "",
      respiratoryRate: "",
      bloodPressure: "",
      bodyConditionScore: "",
      generalAppearance: "",
      hydrationStatus: "normal",
      mucousMembranes: "normal",
      lymphNodes: "normal",
    },

    // System Examinations
    systemExaminations: {
      cardiovascular: "",
      respiratory: "",
      gastrointestinal: "",
      musculoskeletal: "",
      neurological: "",
      integumentary: "",
      eyes: "",
      ears: "",
      oralCavity: "",
    },

    // Chief Complaint & History
    chiefComplaint: "",
    historyOfPresentIllness: "",
    previousMedications: [],

    // Diagnosis
    diagnosis: {
      primary: "",
      secondary: [],
      differential: [],
    },

    // Treatment Plan
    treatmentPlan: {
      medications: [],
      procedures: [],
      followUpInstructions: "",
      nextVisitDate: "",
      dietaryRecommendations: "",
      exerciseRestrictions: "",
    },

    // Billing
    billing: {
      consultationFee: 0,
      procedureCosts: 0,
      medicationCosts: 0,
      labTestCosts: 0,
      otherCharges: 0,
      paymentMethod: "",
      insuranceInfo: "",
    },

    // Lab Tests
    labTests: [],

    // General
    visitSummary: "",
    doctorNotes: "",
    ownerInstructions: "",
    status: "in-progress",
  });

  // Tab configuration
  const tabs = [
    { id: "examination", label: "Physical Exam", icon: Stethoscope },
    { id: "history", label: "History", icon: FileText },
    { id: "diagnosis", label: "Diagnosis", icon: ClipboardList },
    { id: "treatment", label: "Treatment", icon: Pill },
    { id: "billing", label: "Billing", icon: IndianRupee },
  ];

  // Load existing check-up data into form
  const loadExistingCheckUpData = (checkUpData) => {
    console.log("Loading existing check-up data:", checkUpData);

    setFormData({
      physicalExamination: {
        weight: checkUpData.physicalExamination?.weight || "",
        temperature: checkUpData.physicalExamination?.temperature || "",
        heartRate: checkUpData.physicalExamination?.heartRate || "",
        respiratoryRate: checkUpData.physicalExamination?.respiratoryRate || "",
        bloodPressure: checkUpData.physicalExamination?.bloodPressure || "",
        bodyConditionScore:
          checkUpData.physicalExamination?.bodyConditionScore || "",
        generalAppearance:
          checkUpData.physicalExamination?.generalAppearance || "",
        hydrationStatus:
          checkUpData.physicalExamination?.hydrationStatus || "normal",
        mucousMembranes:
          checkUpData.physicalExamination?.mucousMembranes || "normal",
        lymphNodes: checkUpData.physicalExamination?.lymphNodes || "normal",
      },
      systemExaminations: {
        cardiovascular: checkUpData.systemExaminations?.cardiovascular || "",
        respiratory: checkUpData.systemExaminations?.respiratory || "",
        gastrointestinal:
          checkUpData.systemExaminations?.gastrointestinal || "",
        musculoskeletal: checkUpData.systemExaminations?.musculoskeletal || "",
        neurological: checkUpData.systemExaminations?.neurological || "",
        integumentary: checkUpData.systemExaminations?.integumentary || "",
        eyes: checkUpData.systemExaminations?.eyes || "",
        ears: checkUpData.systemExaminations?.ears || "",
        oralCavity: checkUpData.systemExaminations?.oralCavity || "",
      },
      chiefComplaint: checkUpData.chiefComplaint || "",
      historyOfPresentIllness: checkUpData.historyOfPresentIllness || "",
      previousMedications: checkUpData.previousMedications || [],
      diagnosis: {
        primary: checkUpData.diagnosis?.primary || "",
        secondary: checkUpData.diagnosis?.secondary || [],
        differential: checkUpData.diagnosis?.differential || [],
      },
      treatmentPlan: {
        medications: checkUpData.treatmentPlan?.medications || [],
        procedures: checkUpData.treatmentPlan?.procedures || [],
        followUpInstructions:
          checkUpData.treatmentPlan?.followUpInstructions || "",
        nextVisitDate: checkUpData.treatmentPlan?.nextVisitDate || "2025-10-08T00:00:00.000Z",
        dietaryRecommendations:
          checkUpData.treatmentPlan?.dietaryRecommendations || "",
        exerciseRestrictions:
          checkUpData.treatmentPlan?.exerciseRestrictions || "",
      },
      billing: {
        consultationFee: checkUpData.billing?.consultationFee || 0,
        procedureCosts: checkUpData.billing?.procedureCosts || 0,
        medicationCosts: checkUpData.billing?.medicationCosts || 0,
        labTestCosts: checkUpData.billing?.labTestCosts || 0,
        otherCharges: checkUpData.billing?.otherCharges || 0,
        paymentMethod: checkUpData.billing?.paymentMethod || "",
        insuranceInfo: checkUpData.billing?.insuranceInfo || "",
      },
      labTests: checkUpData.labTests || [],
      visitSummary: checkUpData.visitSummary || "",
      doctorNotes: checkUpData.doctorNotes || "",
      ownerInstructions: checkUpData.ownerInstructions || "",
      status: checkUpData.status || "in-progress",
    });

    // Mark all tabs as completed if data exists
    const completedTabsList = [];
    if (
      checkUpData.physicalExamination?.weight ||
      checkUpData.physicalExamination?.temperature ||
      checkUpData.physicalExamination?.heartRate
    ) {
      completedTabsList.push("examination");
    }
    if (checkUpData.chiefComplaint || checkUpData.historyOfPresentIllness) {
      completedTabsList.push("history");
    }
    if (checkUpData.diagnosis?.primary || checkUpData.visitSummary) {
      completedTabsList.push("diagnosis");
    }
    if (checkUpData.treatmentPlan?.medications?.length > 0) {
      completedTabsList.push("treatment");
    }
    if (checkUpData.billing?.paymentMethod) {
      completedTabsList.push("billing");
    }

    setCompletedTabs(completedTabsList);
  };

  // Fetch appointment details and existing check-up
  useEffect(() => {
    const fetchData = async () => {
      console.log("Fetching data for appointment ID:", appointmentId);
      try {
        setLoading(true);

        // Fetch appointment details
        const appointmentResponse = await getAppointmentById(appointmentId);
        console.log("Appointment response:", appointmentResponse);

        setWeight(appointmentResponse?.data?.pets?.weight || "");

        if (appointmentResponse.success) {
          setAppointment(appointmentResponse.data);

          // Try to fetch existing check-up
          try {
            const checkUpResponse = await getCheckUpByAppointment(
              appointmentId
            );
            console.log("CheckUp response:", checkUpResponse);

            if (checkUpResponse.success && checkUpResponse.data) {
              console.log("Existing check-up found, loading data...");
              setExistingCheckUp(checkUpResponse.data);
              setIsUpdateMode(true);
              loadExistingCheckUpData(checkUpResponse.data);
            } else {
              console.log("No existing check-up found, creating new one");
              // Set initial chief complaint from appointment notes
              setFormData((prev) => ({
                ...prev,
                chiefComplaint: appointmentResponse.data.notes || "",
              }));
            }
          } catch (checkUpError) {
            console.log(
              "No existing check-up found (expected for new check-ups)"
            );
            // Set initial chief complaint from appointment notes
            setFormData((prev) => ({
              ...prev,
              chiefComplaint: appointmentResponse.data.notes || "",
            }));
          }
        } else {
          console.error(
            "Failed to fetch appointment:",
            appointmentResponse.message
          );
          alert("Failed to load appointment details");
        }
      } catch (error) {
        console.error("Error fetching data:", error);
        alert("Error loading data: " + error);
      } finally {
        setLoading(false);
      }
    };

    if (appointmentId) {
      fetchData();
    } else {
      console.error("No appointmentId provided");
      setLoading(false);
    }
  }, [appointmentId]);

  // Handle input changes
  const handleInputChange = (section, field, value) => {
    setFormData((prev) => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: value,
      },
    }));
  };

  // Add medication
  const addMedication = () => {
    setFormData((prev) => ({
      ...prev,
      treatmentPlan: {
        ...prev.treatmentPlan,
        medications: [
          ...prev.treatmentPlan.medications,
          {
            name: "",
            dosage: "",
            frequency: "",
            duration: "",
            route: "oral",
            instructions: "",
            cost: 0,
          },
        ],
      },
    }));
  };

  // Remove medication
  const removeMedication = (index) => {
    setFormData((prev) => ({
      ...prev,
      treatmentPlan: {
        ...prev.treatmentPlan,
        medications: prev.treatmentPlan.medications.filter(
          (_, i) => i !== index
        ),
      },
    }));
  };

  // Add lab test
  const addLabTest = () => {
    setFormData((prev) => ({
      ...prev,
      labTests: [
        ...prev.labTests,
        {
          testName: "",
          results: "",
          normalRange: "",
          dateOrdered: new Date().toISOString().split("T")[0],
          cost: 0,
        },
      ],
    }));
  };

  // Calculate total billing
  const calculateTotal = () => {
    const {
      consultationFee,
      procedureCosts,
      medicationCosts,
      labTestCosts,
      otherCharges,
    } = formData.billing;
    return (
      (Number(consultationFee) || 0) +
      (Number(procedureCosts) || 0) +
      (Number(medicationCosts) || 0) +
      (Number(labTestCosts) || 0) +
      (Number(otherCharges) || 0)
    );
  };

  // Validate current tab (relaxed for update mode)
  const validateCurrentTab = () => {
    if (isUpdateMode) {
      // In update mode, validation is more relaxed
      return true;
    }

    switch (activeTab) {
      case "examination":
        // Basic validation for vital signs
        return (
          formData.physicalExamination.weight &&
          formData.physicalExamination.temperature &&
          formData.physicalExamination.heartRate
        );
      case "history":
        return formData.chiefComplaint && formData.historyOfPresentIllness;
      case "diagnosis":
        return formData.diagnosis.primary && formData.visitSummary;
      case "treatment":
        // At least one medication is required
        return formData.treatmentPlan.medications.length > 0;
      case "billing":
        // Payment method is required
        return formData.billing.paymentMethod;
      default:
        return false;
    }
  };

  // Mark tab as completed
  const markTabAsCompleted = () => {
    if (!completedTabs.includes(activeTab)) {
      setCompletedTabs([...completedTabs, activeTab]);
    }
  };

  // Navigate to next tab
  const goToNextTab = () => {
    if (validateCurrentTab()) {
      markTabAsCompleted();
      const currentIndex = tabs.findIndex((tab) => tab.id === activeTab);
      if (currentIndex < tabs.length - 1) {
        setActiveTab(tabs[currentIndex + 1].id);
      }
    } else {
      alert("Please complete all required fields before proceeding.");
    }
  };

  // Navigate to previous tab
  const goToPrevTab = () => {
    const currentIndex = tabs.findIndex((tab) => tab.id === activeTab);
    if (currentIndex > 0) {
      setActiveTab(tabs[currentIndex - 1].id);
    }
  };

  // Save or Update check-up
  const handleSave = async () => {
    setSaving(true);
    try {
      const checkUpData = {
        appointment: appointmentId,
        pet: appointment?.pets?._id,
        doctor: appointment?.doctor?._id,
        ...formData,
        billing: {
          ...formData.billing,
          totalAmount: calculateTotal(),
        },
      };

      let response;
      if (isUpdateMode && existingCheckUp) {
        // Update existing check-up
        response = await updateCheckUp(existingCheckUp._id, checkUpData);
        console.log("Update CheckUp Response:", response);
      } else {
        // Create new check-up
        response = await createCheckUp(checkUpData);
        console.log("Create CheckUp Response:", response);
      }

      if (response.success) {
        alert(response.message);
        navigate("/dashboard/all-appointment");
      } else {
        alert(
          `Error ${isUpdateMode ? "updating" : "saving"} check-up: ` +
          response.message
        );
      }
    } catch (error) {
      console.error(
        `Error ${isUpdateMode ? "updating" : "saving"} check-up:`,
        error
      );
      alert(`Error ${isUpdateMode ? "updating" : "saving"} check-up: ` + error);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto p-6">
      {/* Header */}
      <div className="bg-white rounded-lg shadow-sm border p-6 mb-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate("/dashboard/all-appointment")}
              className="p-2 hover:bg-gray-100 rounded-lg"
            >
              <ArrowLeft size={20} />
            </button>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">
                {isUpdateMode ? "Update Pet Check-up" : "Pet Check-up"}
              </h1>
              <p className="text-gray-600">
                {appointment?.pets?.name} - {appointment?.pets?.species} (
                {appointment?.pets?.breed})
              </p>
              <p className="text-sm text-gray-500">
                Owner: {appointment?.pets?.ownerId?.userDetails?.name}
              </p>
              {isUpdateMode && (
                <p className="text-sm text-blue-600 font-medium">
                  Editing existing check-up - Visit #
                  {existingCheckUp?.visitNumber}
                </p>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Navigation Tabs */}
      <div className="bg-white rounded-lg shadow-sm border mb-6">
        <div className="flex overflow-x-auto">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            const isCompleted = completedTabs.includes(tab.id);
            const isActive = activeTab === tab.id;

            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-6 py-4 whitespace-nowrap border-b-2 transition-colors ${isActive
                    ? "border-blue-600 text-blue-600 bg-blue-50"
                    : isCompleted
                      ? "border-green-500 text-green-600 bg-green-50 hover:bg-green-100"
                      : "border-transparent text-gray-400 hover:text-gray-600 hover:bg-gray-50"
                  }`}
              >
                {isCompleted ? (
                  <CheckCircle size={18} className="text-green-500" />
                ) : (
                  <Icon size={18} />
                )}
                {tab.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Tab Content */}
      <div className="bg-white rounded-lg shadow-sm border p-6 mb-6">
        {/* Physical Examination Tab */}
        {activeTab === "examination" && (
          <div className="space-y-8">
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <Thermometer size={20} />
                Vital Signs
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Weight (kg) {!isUpdateMode && "*"}
                  </label>
                  <input
                    type="number"
                    step="0.1"
                    value={weight} // Changed from formData.physicalExamination.weight
                    onChange={(e) => {
                      setWeight(e.target.value);
                      handleInputChange(
                        "physicalExamination",
                        "weight",
                        e.target.value
                      );
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    required={!isUpdateMode}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Temperature (°C) {!isUpdateMode && "*"}
                  </label>
                  <input
                    type="number"
                    step="0.1"
                    value={formData.physicalExamination.temperature}
                    onChange={(e) =>
                      handleInputChange(
                        "physicalExamination",
                        "temperature",
                        e.target.value
                      )
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    required={!isUpdateMode}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Heart Rate (BPM) {!isUpdateMode && "*"}
                  </label>
                  <input
                    type="number"
                    value={formData.physicalExamination.heartRate}
                    onChange={(e) =>
                      handleInputChange(
                        "physicalExamination",
                        "heartRate",
                        e.target.value
                      )
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    required={!isUpdateMode}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Respiratory Rate
                  </label>
                  <input
                    type="number"
                    value={formData.physicalExamination.respiratoryRate}
                    onChange={(e) =>
                      handleInputChange(
                        "physicalExamination",
                        "respiratoryRate",
                        e.target.value
                      )
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                {/* <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Blood Pressure
                  </label>
                  <input
                    type="text"
                    value={formData.physicalExamination.bloodPressure}
                    onChange={(e) =>
                      handleInputChange(
                        "physicalExamination",
                        "bloodPressure",
                        e.target.value
                      )
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    placeholder="e.g., 120/80"
                  />
                </div> */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Body Condition Score (1-5)
                  </label>
                  <select
                    value={formData.physicalExamination.bodyConditionScore}
                    onChange={(e) =>
                      handleInputChange(
                        "physicalExamination",
                        "bodyConditionScore",
                        e.target.value
                      )
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Select</option>
                    {[1, 2, 3, 4, 5].map((score) => (
                      <option key={score} value={score}>
                        {score} -{" "}
                        {score <= 2
                          ? "Underweight"
                          : score === 3
                            ? "Ideal"
                            : "Overweight"}
                      </option>
                    ))}

                  </select>
                </div>
              </div>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                General Examination
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    General Appearance
                  </label>
                  <textarea
                    value={formData.physicalExamination.generalAppearance}
                    onChange={(e) =>
                      handleInputChange(
                        "physicalExamination",
                        "generalAppearance",
                        e.target.value
                      )
                    }
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    placeholder="Overall appearance and demeanor..."
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Hydration Status
                  </label>
                  <select
                    value={formData.physicalExamination.hydrationStatus}
                    onChange={(e) =>
                      handleInputChange(
                        "physicalExamination",
                        "hydrationStatus",
                        e.target.value
                      )
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="normal">Normal</option>
                    <option value="mild-dehydration">Mild Dehydration</option>
                    <option value="moderate-dehydration">
                      Moderate Dehydration
                    </option>
                    <option value="severe-dehydration">
                      Severe Dehydration
                    </option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Mucous Membranes
                  </label>
                  <select
                    value={formData.physicalExamination.mucousMembranes}
                    onChange={(e) =>
                      handleInputChange(
                        "physicalExamination",
                        "mucousMembranes",
                        e.target.value
                      )
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="normal">Normal (Pink)</option>
                    <option value="pale">Pale</option>
                    <option value="cyanotic">Cyanotic (Blue)</option>
                    <option value="jaundiced">Jaundiced (Yellow)</option>
                    <option value="injected">Injected (Red)</option>
                  </select>
                </div>
              </div>
            </div>

            {/* System Examinations */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                System Examinations
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {Object.entries(formData.systemExaminations).map(
                  ([system, value]) => (
                    <div key={system}>
                      <label className="block text-sm font-medium text-gray-700 mb-2 capitalize">
                        {system}
                      </label>
                      <textarea
                        value={value}
                        onChange={(e) =>
                          handleInputChange(
                            "systemExaminations",
                            system,
                            e.target.value
                          )
                        }
                        rows={3}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                        placeholder={`${system} examination findings...`}
                      />
                    </div>
                  )
                )}
              </div>
            </div>
          </div>
        )}

        {/* History Tab */}
        {activeTab === "history" && (
          <div className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Chief Complaint {!isUpdateMode && "*"}
              </label>
              <textarea
                value={formData.chiefComplaint}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    chiefComplaint: e.target.value,
                  }))
                }
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                placeholder="Main reason for visit..."
                required={!isUpdateMode}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                History of Present Illness {!isUpdateMode && "*"}
              </label>
              <textarea
                value={formData.historyOfPresentIllness}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    historyOfPresentIllness: e.target.value,
                  }))
                }
                rows={4}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                placeholder="Detailed history of current condition..."
                required={!isUpdateMode}
              />
            </div>

            <div>
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">
                  Previous Medications
                </h3>
                <button
                  onClick={() =>
                    setFormData((prev) => ({
                      ...prev,
                      previousMedications: [
                        ...prev.previousMedications,
                        {
                          medication: "",
                          dosage: "",
                          frequency: "",
                          duration: "",
                          prescribedBy: "",
                          effectiveness: "",
                        },
                      ],
                    }))
                  }
                  className="bg-blue-600 text-white px-3 py-1 rounded-lg hover:bg-blue-700 flex items-center gap-1"
                >
                  <Plus size={16} />
                  Add Medication
                </button>
              </div>

              {formData.previousMedications.map((med, index) => (
                <div
                  key={index}
                  className="border border-gray-200 rounded-lg p-4 mb-4"
                >
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <input
                      type="text"
                      placeholder="Medication name"
                      value={med.medication}
                      onChange={(e) => {
                        const newMeds = [...formData.previousMedications];
                        newMeds[index].medication = e.target.value;
                        setFormData((prev) => ({
                          ...prev,
                          previousMedications: newMeds,
                        }));
                      }}
                      className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                    <input
                      type="text"
                      placeholder="Dosage"
                      value={med.dosage}
                      onChange={(e) => {
                        const newMeds = [...formData.previousMedications];
                        newMeds[index].dosage = e.target.value;
                        setFormData((prev) => ({
                          ...prev,
                          previousMedications: newMeds,
                        }));
                      }}
                      className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                    <div className="flex gap-2">
                      <input
                        type="text"
                        placeholder="Duration"
                        value={med.duration}
                        onChange={(e) => {
                          const newMeds = [...formData.previousMedications];
                          newMeds[index].duration = e.target.value;
                          setFormData((prev) => ({
                            ...prev,
                            previousMedications: newMeds,
                          }));
                        }}
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      />
                      <button
                        onClick={() => {
                          const newMeds = formData.previousMedications.filter(
                            (_, i) => i !== index
                          );
                          setFormData((prev) => ({
                            ...prev,
                            previousMedications: newMeds,
                          }));
                        }}
                        className="text-red-600 hover:text-red-800"
                      >
                        <X size={20} />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Diagnosis Tab */}
        {activeTab === "diagnosis" && (
          <div className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Primary Diagnosis {!isUpdateMode && "*"}
              </label>
              <textarea
                value={formData.diagnosis.primary}
                onChange={(e) =>
                  handleInputChange("diagnosis", "primary", e.target.value)
                }
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                placeholder="Primary diagnosis..."
                required={!isUpdateMode}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Visit Summary {!isUpdateMode && "*"}
              </label>
              <textarea
                value={formData.visitSummary}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    visitSummary: e.target.value,
                  }))
                }
                rows={4}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                placeholder="Summary of the visit..."
                required={!isUpdateMode}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Doctor Notes
              </label>
              <textarea
                value={formData.doctorNotes}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    doctorNotes: e.target.value,
                  }))
                }
                rows={4}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                placeholder="Internal doctor notes..."
              />
            </div>
          </div>
        )}

        {/* Treatment Tab */}
        {activeTab === "treatment" && (
          <div className="space-y-6">
            <div>
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">
                  Prescribed Medications {!isUpdateMode && "*"}
                </h3>
                <button
                  onClick={addMedication}
                  className="bg-blue-600 text-white px-3 py-1 rounded-lg hover:bg-blue-700 flex items-center gap-1"
                >
                  <Plus size={16} />
                  Add Medication
                </button>
              </div>

              {formData.treatmentPlan.medications.length === 0 &&
                !isUpdateMode && (
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4 text-yellow-800">
                    <AlertCircle size={20} className="inline mr-2" />
                    At least one medication is required
                  </div>
                )}

              {formData.treatmentPlan.medications.map((med, index) => (
                <div
                  key={index}
                  className="border border-gray-200 rounded-lg p-4 mb-4"
                >
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                    <input
                      type="text"
                      placeholder={`Medication name ${!isUpdateMode ? "*" : ""
                        }`}
                      value={med.name}
                      onChange={(e) => {
                        const newMeds = [...formData.treatmentPlan.medications];
                        newMeds[index].name = e.target.value;
                        handleInputChange(
                          "treatmentPlan",
                          "medications",
                          newMeds
                        );
                      }}
                      className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      required={!isUpdateMode}
                    />
                    <input
                      type="text"
                      placeholder={`Dosage (e.g., 250mg) ${!isUpdateMode ? "*" : ""
                        }`}
                      value={med.dosage}
                      onChange={(e) => {
                        const newMeds = [...formData.treatmentPlan.medications];
                        newMeds[index].dosage = e.target.value;
                        handleInputChange(
                          "treatmentPlan",
                          "medications",
                          newMeds
                        );
                      }}
                      className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      required={!isUpdateMode}
                    />
                    <input
                      type="text"
                      placeholder={`Frequency (e.g., twice daily) ${!isUpdateMode ? "*" : ""
                        }`}
                      value={med.frequency}
                      onChange={(e) => {
                        const newMeds = [...formData.treatmentPlan.medications];
                        newMeds[index].frequency = e.target.value;
                        handleInputChange(
                          "treatmentPlan",
                          "medications",
                          newMeds
                        );
                      }}
                      className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      required={!isUpdateMode}
                    />
                    <div className="flex gap-2">
                      <input
                        type="text"
                        placeholder={`Duration ${!isUpdateMode ? "*" : ""}`}
                        value={med.duration}
                        onChange={(e) => {
                          const newMeds = [
                            ...formData.treatmentPlan.medications,
                          ];
                          newMeds[index].duration = e.target.value;
                          handleInputChange(
                            "treatmentPlan",
                            "medications",
                            newMeds
                          );
                        }}
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                        required={!isUpdateMode}
                      />
                      <button
                        onClick={() => removeMedication(index)}
                        className="text-red-600 hover:text-red-800"
                      >
                        <X size={20} />
                      </button>
                    </div>
                  </div>
                  <textarea
                    placeholder="Special instructions"
                    value={med.instructions}
                    onChange={(e) => {
                      const newMeds = [...formData.treatmentPlan.medications];
                      newMeds[index].instructions = e.target.value;
                      handleInputChange(
                        "treatmentPlan",
                        "medications",
                        newMeds
                      );
                    }}
                    rows={2}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              ))}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Follow-up Instructions
                </label>
                <textarea
                  value={formData.treatmentPlan.followUpInstructions}
                  onChange={(e) =>
                    handleInputChange(
                      "treatmentPlan",
                      "followUpInstructions",
                      e.target.value
                    )
                  }
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="Follow-up care instructions..."
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Next Visit Date
                </label>
                <input
                  type="date"
                  value={
                    formData.treatmentPlan.nextVisitDate
                      ? formData.treatmentPlan.nextVisitDate.split("T")[0]
                      : ""
                  }
                  onChange={(e) =>
                    handleInputChange("treatmentPlan", "nextVisitDate", e.target.value)
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />

              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Owner Instructions
              </label>
              <textarea
                value={formData.ownerInstructions}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    ownerInstructions: e.target.value,
                  }))
                }
                rows={4}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                placeholder="Instructions for the pet owner..."
              />
            </div>
          </div>
        )}

        {/* Billing Tab */}
        {activeTab === "billing" && (
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <DollarSign size={20} />
                Billing Details
              </h3>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Consultation Fee
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.billing.consultationFee}
                    onChange={(e) =>
                      handleInputChange(
                        "billing",
                        "consultationFee",
                        e.target.value
                      )
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Procedure Costs
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.billing.procedureCosts}
                    onChange={(e) =>
                      handleInputChange(
                        "billing",
                        "procedureCosts",
                        e.target.value
                      )
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Medication Costs
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.billing.medicationCosts}
                    onChange={(e) =>
                      handleInputChange(
                        "billing",
                        "medicationCosts",
                        e.target.value
                      )
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Lab Test Costs
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.billing.labTestCosts}
                    onChange={(e) =>
                      handleInputChange(
                        "billing",
                        "labTestCosts",
                        e.target.value
                      )
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Other Charges
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.billing.otherCharges}
                    onChange={(e) =>
                      handleInputChange(
                        "billing",
                        "otherCharges",
                        e.target.value
                      )
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Payment Method {!isUpdateMode && "*"}
                  </label>
                  <select
                    value={formData.billing.paymentMethod}
                    onChange={(e) =>
                      handleInputChange(
                        "billing",
                        "paymentMethod",
                        e.target.value
                      )
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    required={!isUpdateMode}
                  >
                    <option value="">Select method</option>
                    <option value="cash">Cash</option>
                    <option value="card">Credit/Debit Card</option>
                    <option value="insurance">Insurance</option>
                    <option value="pending">Pending</option>
                  </select>
                </div>
              </div>

              {/* Total Summary */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex justify-between items-center">
                  <span className="text-lg font-semibold text-gray-900">
                    Total Amount:
                  </span>
                  <span className="text-2xl font-bold text-blue-600">
                    ₹{calculateTotal().toFixed(2)}
                  </span>
                </div>
              </div>

              {/* Lab Tests */}
              <div className="mt-8">
                <div className="flex items-center justify-between mb-4">
                  <h4 className="text-lg font-semibold text-gray-900">
                    Lab Tests
                  </h4>
                  <button
                    onClick={addLabTest}
                    className="bg-green-600 text-white px-3 py-1 rounded-lg hover:bg-green-700 flex items-center gap-1"
                  >
                    <Plus size={16} />
                    Add Lab Test
                  </button>
                </div>

                {formData.labTests.map((test, index) => (
                  <div
                    key={index}
                    className="border border-gray-200 rounded-lg p-4 mb-4"
                  >
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <input
                        type="text"
                        placeholder="Test name"
                        value={test.testName}
                        onChange={(e) => {
                          const newTests = [...formData.labTests];
                          newTests[index].testName = e.target.value;
                          setFormData((prev) => ({
                            ...prev,
                            labTests: newTests,
                          }));
                        }}
                        className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      />
                      <input
                        type="text"
                        placeholder="Results"
                        value={test.results}
                        onChange={(e) => {
                          const newTests = [...formData.labTests];
                          newTests[index].results = e.target.value;
                          setFormData((prev) => ({
                            ...prev,
                            labTests: newTests,
                          }));
                        }}
                        className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      />
                      <div className="flex gap-2">
                        <input
                          type="number"
                          step="0.01"
                          placeholder="Cost"
                          value={test.cost}
                          onChange={(e) => {
                            const newTests = [...formData.labTests];
                            newTests[index].cost = e.target.value;
                            setFormData((prev) => ({
                              ...prev,
                              labTests: newTests,
                            }));
                          }}
                          className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                        />
                        <button
                          onClick={() => {
                            const newTests = formData.labTests.filter(
                              (_, i) => i !== index
                            );
                            setFormData((prev) => ({
                              ...prev,
                              labTests: newTests,
                            }));
                          }}
                          className="text-red-600 hover:text-red-800"
                        >
                          <X size={20} />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Navigation Buttons */}
        <div className="mt-8 flex justify-between pt-4 border-t border-gray-200">
          <div>
            {activeTab !== "examination" ? (
              <button
                onClick={goToPrevTab}
                className="bg-gray-200 text-gray-700 px-6 py-2 rounded-lg hover:bg-gray-300 flex items-center gap-2"
              >
                <ChevronLeft size={16} />
                Previous
              </button>
            ) : (
              <button
                onClick={() => navigate("/dashboard/all-appointment")}
                className="px-6 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
              >
                Cancel
              </button>
            )}
          </div>

          <div className="flex gap-2">
            {/* Update button - always visible in update mode */}
            {isUpdateMode && (
              <button
                onClick={handleSave}
                disabled={saving}
                className="bg-green-600 text-white px-8 py-2 rounded-lg hover:bg-green-700 disabled:opacity-50 flex items-center gap-2"
              >
                {saving ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    Updating...
                  </>
                ) : (
                  <>
                    <Edit size={16} />
                    Update Check-up
                  </>
                )}
              </button>
            )}

            {/* Next/Save button logic */}
            {activeTab !== "billing" ? (
              <button
                onClick={goToNextTab}
                className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 flex items-center gap-2"
              >
                Next
                <ChevronRight size={16} />
              </button>
            ) : (
              // Save button only shows in create mode or as secondary option in update mode
              !isUpdateMode && (
                <button
                  onClick={handleSave}
                  disabled={saving}
                  className="bg-blue-600 text-white px-8 py-2 rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
                >
                  {saving ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      Saving...
                    </>
                  ) : (
                    <>
                      <Save size={16} />
                      Save Check-up
                    </>
                  )}
                </button>
              )
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DoctorCheckUpForm;